/**
 * Renders either a preset SVG (via `<img>` with mask trick so it
 * inherits `currentColor`) or a custom uploaded raster image
 * (PNG/WebP — used as-is, NOT color-masked since the operator picked
 * its colors deliberately).
 *
 * Falls back to a generic question-mark glyph if the icon URL fails
 * to load (broken custom URL after deletion / typo).
 */
import { useState, type ReactElement } from 'react';
import type { ShortcutIcon as ShortcutIconType } from '@nxorbit/shared-schema';
import { resolvePresetIcon } from './shortcut-icons';

interface Props {
  icon: ShortcutIconType;
  /** Tailwind sizing classes — default `h-6 w-6`. */
  className?: string;
  /** A11y label (overrides the preset's default Chinese label). */
  ariaLabel?: string;
}

export function ShortcutIcon({ icon, className = 'h-6 w-6', ariaLabel }: Props): ReactElement {
  const [errored, setErrored] = useState(false);

  if (errored) {
    return (
      <span
        aria-label={ariaLabel ?? '未知圖示'}
        className={`${className} inline-flex items-center justify-center text-current opacity-60`}
      >
        ?
      </span>
    );
  }

  if (icon.kind === 'preset') {
    const meta = resolvePresetIcon(icon.id);
    // CSS mask lets us colorise the monochrome SVG via `background-color: currentColor`.
    // Works in every modern browser; admin + embed both target evergreen.
    return (
      <span
        aria-label={ariaLabel ?? meta.label}
        role="img"
        className={`${className} inline-block bg-current`}
        style={{
          WebkitMaskImage: `url(${meta.src})`,
          maskImage: `url(${meta.src})`,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
        }}
      />
    );
  }

  // Custom upload — render the raster as-is (operator-controlled colors).
  return (
    <img
      src={icon.url}
      alt={ariaLabel ?? '快捷鍵圖示'}
      className={`${className} object-contain`}
      onError={() => setErrored(true)}
      draggable={false}
    />
  );
}

export default ShortcutIcon;
